import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../API';
import { extractData } from '../../utils/apiResponse';
import { getRegionDisplayLabel } from '../../utils/stainRegionFields';
import styles from './BdsaItemsTable.module.css';

const PAGE_SIZE = 25;

function BdsaItemsTable() {
    const [data, setData] = useState({ data: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [offset, setOffset] = useState(0);
    const [sort, setSort] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [filenameSubstring, setFilenameSubstring] = useState('');
    const [caseId, setCaseId] = useState('');
    const [stainId, setStainId] = useState('');
    const [magnification, setMagnification] = useState('');

    const queryParams = {
        limit: PAGE_SIZE,
        offset,
        sort,
        sortOrder,
        ...(filenameSubstring.trim() && { filenameSubstring: filenameSubstring.trim() }),
        ...(caseId.trim() && { caseId: caseId.trim() }),
        ...(stainId.trim() && { stainId: stainId.trim() }),
        ...(magnification.trim() && { magnification: magnification.trim() }),
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        const queryString = Object.entries(queryParams)
            .filter(([, v]) => v != null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join('&');
        api.get(`bdsa/items?${queryString}`)
            .then((response) => {
                if (cancelled) return;
                if (response?.status === 200 && response?.data) {
                    setData({ data: response.data.data || [], total: response.data.total ?? 0 });
                } else {
                    setError(response?.data?.message || 'Failed to load BDSA items');
                    setData({ data: [], total: 0 });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err?.message || 'Failed to load BDSA items');
                    setData({ data: [], total: 0 });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [offset, sort, sortOrder, filenameSubstring, caseId, stainId, magnification]);

    const total = data.total;
    const rows = data.data || [];
    const start = offset + 1;
    const end = Math.min(offset + PAGE_SIZE, total);
    const hasNext = offset + PAGE_SIZE < total;
    const hasPrev = offset > 0;

    const stainDisplay = (stain) => {
        if (stain == null) return '—';
        if (typeof stain === 'object' && stain.name != null) return stain.name;
        return typeof stain === 'string' ? stain : '—';
    };

    const regionsSummary = (regions) => {
        if (!Array.isArray(regions) || regions.length === 0) return '—';
        return regions.map((r) => getRegionDisplayLabel(r)).join(', ');
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <h2>BDSA Items</h2>
                <Link to="/collections" className={styles.link}>Browse by collection</Link>
            </div>
            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Filename contains..."
                    value={filenameSubstring}
                    onChange={(e) => { setFilenameSubstring(e.target.value); setOffset(0); }}
                    aria-label="Filter by filename"
                />
                <input
                    type="text"
                    placeholder="Case ID"
                    value={caseId}
                    onChange={(e) => { setCaseId(e.target.value); setOffset(0); }}
                    aria-label="Filter by case ID"
                />
                <input
                    type="text"
                    placeholder="Stain ID"
                    value={stainId}
                    onChange={(e) => { setStainId(e.target.value); setOffset(0); }}
                    aria-label="Filter by stain ID"
                />
                <input
                    type="text"
                    placeholder="Magnification"
                    value={magnification}
                    onChange={(e) => { setMagnification(e.target.value); setOffset(0); }}
                    aria-label="Filter by magnification"
                />
                <select
                    value={sort}
                    onChange={(e) => { setSort(e.target.value); setOffset(0); }}
                    aria-label="Sort by"
                >
                    <option value="updatedAt">Sort by updated</option>
                    <option value="networkFilename">Sort by filename</option>
                    <option value="originalFilename">Sort by original filename</option>
                </select>
                <select
                    value={String(sortOrder)}
                    onChange={(e) => { setSortOrder(parseInt(e.target.value, 10)); setOffset(0); }}
                    aria-label="Sort order"
                >
                    <option value="-1">Newest first</option>
                    <option value="1">Oldest first</option>
                </select>
            </div>
            {error && <div className={styles.error}>{error}</div>}
            {loading ? (
                <div className={styles.loading}>Loading…</div>
            ) : (
                <>
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Name / Filename</th>
                                    <th>Case ID</th>
                                    <th>Stain</th>
                                    <th>Regions</th>
                                    <th>Magnification</th>
                                    <th>Level</th>
                                    <th>Scan</th>
                                    <th>Link</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={8}>No BDSA items found.</td>
                                    </tr>
                                ) : (
                                    rows.map((row) => (
                                        <tr key={row.bdsaItemId}>
                                            <td>{row.networkFilename || row.originalFilename || '—'}</td>
                                            <td>{row.bdsaCaseId || '—'}</td>
                                            <td>{stainDisplay(row.stain)}</td>
                                            <td>{regionsSummary(row.regions)}</td>
                                            <td>{row.magnification ?? '—'}</td>
                                            <td>{row.level ?? '—'}</td>
                                            <td>{row.scan ?? '—'}</td>
                                            <td>
                                                {row.itemId ? (
                                                    <Link to={`/item/${row.itemId}`} className={styles.link}>Open</Link>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className={styles.pagination}>
                        <span className={styles.summary}>
                            Showing {total === 0 ? 0 : start}–{end} of {total}
                        </span>
                        <button type="button" onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))} disabled={!hasPrev}>
                            Previous
                        </button>
                        <button type="button" onClick={() => setOffset((o) => o + PAGE_SIZE)} disabled={!hasNext}>
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default BdsaItemsTable;
